import React, { createContext, useCallback, useContext, useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import SessionQuestionsDialog from '../components/Dashboard/SessionQuestionsDialog';
import SessionClosedPrompt from '../components/Dashboard/SessionClosedPrompt';
import TabloulDialog from '../components/Dashboard/TabloulDialog';
import { useDashboardDataContext } from './DashboardDataContext';

interface SessionQuestionsContextType {
  /** Opens the memento; pass a case id to preselect it for the quick note. */
  openSessionQuestions: (caseId?: string) => void;
  /** Opens the tablou (the four-layer deep dive) for one specific case and session. */
  openTablou: (input: { caseId: string; sessionNumber: number }) => void;
  /** Declines a session's tablou for good — "Sari peste" in the prepare-session briefing. */
  dismissTablouAsk: (input: { caseId: string; sessionNumber: number }) => void;
  /**
   * Offers the tablou right after a report is filed. Lives here so any screen that can save a
   * report — dashboard, cases, the reports page — hands over the same way.
   *
   * Note: `Case.pendingClarifySession` (the "Sesiunea N neclarificată" ask) is written by the
   * report save itself (`SessionReport.tsx`), not by what happens to this popup — so it survives
   * "Acum" abandoned without saving, and the popup never being answered at all. Saving that
   * session's tablou clears it.
   */
  offerAfterReport: (input: { caseId: string; sessionNumber: number | null }) => void;
}

const SessionQuestionsContext = createContext<SessionQuestionsContextType | null>(null);

export function useSessionQuestions(): SessionQuestionsContextType {
  const context = useContext(SessionQuestionsContext);
  if (!context) {
    throw new Error('useSessionQuestions must be used within SessionQuestionsProvider');
  }
  return context;
}

/**
 * Holds the memento and the tablou so anywhere can open either — the top bar reaches for the
 * memento cold, the prepare-session briefing hands it the case it was already showing, the
 * session-closed prompt and the "neclarificată" alerts reach for the tablou with a specific case
 * and session already in hand.
 *
 * A context rather than a dialog per call site: two instances would mean two pieces of state for
 * one thing on screen, and the call sites live in different trees from the top bar.
 */
export const SessionQuestionsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { cases, ownCases, sessionReportCounts, upsertCase } = useDashboardDataContext();
  const [open, setOpen] = useState(false);
  const [preselectedCaseId, setPreselectedCaseId] = useState<string | undefined>(undefined);
  const [closed, setClosed] = useState<{ caseId: string; sessionNumber: number | null } | null>(
    null
  );
  const [tablouOpen, setTablouOpen] = useState(false);
  const [tablouTarget, setTablouTarget] = useState<{
    caseId: string;
    sessionNumber: number;
  } | null>(null);

  const openSessionQuestions = useCallback((caseId?: string) => {
    setPreselectedCaseId(caseId);
    setOpen(true);
  }, []);

  const openTablou = useCallback((input: { caseId: string; sessionNumber: number }) => {
    setTablouTarget(input);
    setTablouOpen(true);
  }, []);

  const offerAfterReport = useCallback(
    (input: { caseId: string; sessionNumber: number | null }) => setClosed(input),
    []
  );

  const closedCase = closed ? ownCases.find((c) => c.id === closed.caseId) : undefined;
  // Looked up in every case, not just the owned ones: a tablou is also reachable from the notes
  // of a shadowed case, and those are the apprentice's to work on too.
  const tablouCase = tablouTarget
    ? cases.find((c) => c.id === tablouTarget.caseId)
    : undefined;

  /**
   * Answers the ask for one session — by writing its tablou, or by declining it outright. Both
   * land on the same field, because both mean the same thing: this session is no longer waiting
   * on anything. Guarded on the session the ask is actually pointing at, so reopening an older
   * tablou from its note answers nothing.
   */
  const answerTabloulAsk = useCallback(
    (caseId: string, sessionNumber: number) => {
      const caseItem = cases.find((c) => c.id === caseId);
      if (!caseItem || caseItem.pendingClarifySession !== sessionNumber) return;
      upsertCase({ ...caseItem, pendingClarifySession: null });
      void updateDoc(doc(db, 'cases', caseId), { pendingClarifySession: null }).catch((err) =>
        console.error('Failed to clear the pending clarification:', err)
      );
    },
    [cases, upsertCase]
  );

  const handleTabloulSaved = useCallback(
    (sessionNumber: number) => {
      if (tablouTarget?.caseId) answerTabloulAsk(tablouTarget.caseId, sessionNumber);
    },
    [tablouTarget, answerTabloulAsk]
  );

  const dismissTablouAsk = useCallback(
    (input: { caseId: string; sessionNumber: number }) =>
      answerTabloulAsk(input.caseId, input.sessionNumber),
    [answerTabloulAsk]
  );

  return (
    <SessionQuestionsContext.Provider
      value={{ openSessionQuestions, openTablou, dismissTablouAsk, offerAfterReport }}
    >
      {children}
      <SessionQuestionsDialog
        open={open}
        onClose={() => setOpen(false)}
        cases={ownCases}
        sessionReportCounts={sessionReportCounts}
        preselectedCaseId={preselectedCaseId}
      />
      <SessionClosedPrompt
        open={closed !== null}
        caseName={closedCase?.counseledName ?? ''}
        sessionNumber={closed?.sessionNumber ?? null}
        onNow={() => {
          const caseId = closed?.caseId;
          const sessionNumber = closed?.sessionNumber;
          setClosed(null);
          if (caseId && sessionNumber !== null && sessionNumber !== undefined) {
            openTablou({ caseId, sessionNumber });
          }
        }}
        onLater={() => setClosed(null)}
      />
      <TabloulDialog
        open={tablouOpen}
        onClose={() => setTablouOpen(false)}
        onSaved={handleTabloulSaved}
        caseId={tablouTarget?.caseId ?? ''}
        caseTitle={tablouCase?.title ?? ''}
        caseName={tablouCase?.counseledName ?? ''}
        sessionNumber={tablouTarget?.sessionNumber ?? 0}
      />
    </SessionQuestionsContext.Provider>
  );
};
